import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import axios from "axios";
import { API_ESA_OFFICE } from "utils";
import { saveAs } from "file-saver";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDProgress from "components/MDProgress";

import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Material Dashboard 2 PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

import { logoutUser } from "actions/AuthAction";
import { API_TIMEOUT } from "utils";

import { getDatesWithoutWeekends, hitungtotaljamkerja } from "utils/func";

function KinerjaDH() {
  const [kinerjaData, setKinerjaData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [dinasId, setDinasId] = useState(null);
  const [unitKerja, setUnitKerja] = useState(null);
  const [listLibur, setListLibur] = useState([]);
  const [tanggalAwal, setTanggalAwal] = useState(null);
  const [tanggalAkhir, setTanggalAkhir] = useState(null);
  const [totalHariKerja, setTotalHariKerja] = useState(0);
  const [totalWorkingHours, setTotalWorkingHours] = useState(0);

  // Default tanggal: awal bulan sampai hari ini
  const getDefaultDates = useCallback(() => {
    // Set default tanggal dengan WITA (UTC+8)
    const today = new Date();
    const witaOffset = 8 * 60; // UTC+8 dalam menit
    const witaDate = new Date(today.getTime() + witaOffset * 60 * 1000);

    const currentYear = witaDate.getFullYear();
    const currentMonth = witaDate.getMonth(); // 0-11

    // Buat tanggal 1 bulan berjalan
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);

    const result = {
      tAwal: firstDayOfMonth.toISOString().split("T")[0],
      tAkhir: witaDate.toISOString().split("T")[0],
    };

    return result;
  }, []);

  const [dateRange, setDateRange] = useState(() => getDefaultDates());
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Fungsi untuk menghitung rekap kinerja
  const calculatePerformanceStats = () => {
    const stats = {
      totalPegawai: kinerjaData.length,
      totalWorkingHours: 0,
      rataRataWorkingHours: 0,
      pegawaiTerbaik: null,
      pegawaiTerendah: null,
    };

    if (kinerjaData.length > 0) {
      // Hitung total working hours
      kinerjaData.forEach((item) => {
        if (item.workingHours && item.workingHours !== "0:00") {
          const [hours, minutes] = item.workingHours.split(":").map(Number);
          stats.totalWorkingHours += hours * 60 + minutes;
        }
      });

      // Rata-rata working hours
      stats.rataRataWorkingHours = Math.round(
        stats.totalWorkingHours / kinerjaData.length
      );

      // Pegawai dengan working hours tertinggi dan terendah
      stats.pegawaiTerbaik = kinerjaData[0]; // Sudah diurutkan dari tertinggi
      stats.pegawaiTerendah = kinerjaData[kinerjaData.length - 1]; // Terendah
    }

    return stats;
  };

  const performanceStats = calculatePerformanceStats();

  // Fungsi untuk menghitung persentase working hours
  const calculatePercentage = (workingHours) => {
    if (!workingHours || workingHours === "0:00" || !totalWorkingHours) {
      return 0;
    }

    // Konversi workingHours ke menit
    const [hours, minutes] = workingHours.split(":").map(Number);
    const workingMinutes = hours * 60 + minutes;

    // Konversi totalWorkingHours ke menit (jika berupa string)
    let totalMinutes;
    if (typeof totalWorkingHours === "string") {
      const [totalHours, totalMins] = totalWorkingHours.split(":").map(Number);
      totalMinutes = totalHours * 60 + totalMins;
    } else {
      totalMinutes = totalWorkingHours;
    }

    if (totalMinutes === 0) return 0;

    const percentage = (workingMinutes / totalMinutes) * 100;
    return Math.round(percentage * 100) / 100; // Round to 2 decimal places
  };

  const getLibur = useCallback(() => {
    axios({
      method: "get",
      url: "https://smartservice.rotendaokab.go.id/api/admin/libur",
      timeout: API_TIMEOUT,
      headers: {
        Authorization: `Bearer ${""}`,
      },
    })
      .then((response) => {
        if (response.status === 200) {
          setListLibur(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching libur data:", error);
      });
  }, []);

  const fetchKinerjaData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/instansi/reguler/update",
        {
          tAwal: dateRange.tAwal,
          tAkhir: dateRange.tAkhir,
          unitKerja: unitKerja,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success === 1) {
        // Urutkan data berdasarkan working hours tertinggi ke terendah
        const sortedData = response.data.data.sort((a, b) => {
          // Konversi working hours ke menit untuk perbandingan
          const getMinutes = (timeStr) => {
            if (!timeStr || timeStr === "0:00") return 0;
            const [hours, minutes] = timeStr.split(":").map(Number);
            return hours * 60 + minutes;
          };

          const minutesA = getMinutes(a.workingHours);
          const minutesB = getMinutes(b.workingHours);

          return minutesB - minutesA; // Descending (tertinggi ke terendah)
        });

        setKinerjaData(sortedData);
      } else {
        setKinerjaData([]);
        swal({
          title: "Info",
          text: "Tidak ada data kinerja untuk periode yang dipilih",
          icon: "info",
          button: "OK",
        });
      }
    } catch (error) {
      console.error("Error fetching kinerja data:", error);
      swal({
        title: "Error!",
        text: "Terjadi kesalahan saat mengambil data kinerja",
        icon: "error",
        button: "OK",
      });
      setKinerjaData([]);
    } finally {
      setLoading(false);
    }
  }, [dateRange, unitKerja]);

  const handleDetailClick = (pegawai) => {
    // Navigate ke halaman detail dengan parameter uid dan query parameters untuk tanggal
    navigate(
      `/pages/smartDH/kinerja/detail/${pegawai.uid}?tAwal=${dateRange.tAwal}&tAkhir=${dateRange.tAkhir}&nama=${pegawai.nama}`
    );
  };

  const handleExportExcel = () => {
    if (!kinerjaData.length) {
      swal({
        title: "Info",
        text: "Tidak ada data untuk di-export",
        icon: "info",
        button: "OK",
      });
      return;
    }

    // Buat HTML table untuk Excel
    const tableHeaders = [
      "No",
      "Nama",
      "Hadir",
      "Izin",
      "Sakit",
      "Dinas Luar",
      "Tugas Belajar",
      "Cuti",
      "Jam Kerja",
      "Persentase (%)",
    ];

    let tableRows = "";
    kinerjaData.forEach((row, index) => {
      const percentage = calculatePercentage(row.workingHours);
      tableRows += `
        <tr>
          <td>${index + 1}</td>
          <td>${row.nama || ""}</td>
          <td>${row.hadir || 0}</td>
          <td>${row.izin || 0}</td>
          <td>${row.sakit || 0}</td>
          <td>${row.dinasLuar || 0}</td>
          <td>${row.tugasBelajar || 0}</td>
          <td>${row.cuti || 0}</td>
          <td>${row.workingHours || "0:00"}</td>
          <td>${percentage}</td>
        </tr>
      `;
    });

    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8">
          <title>Kinerja - ${dateRange.tAwal} s/d ${dateRange.tAkhir}</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Kinerja Smart DH - ${dateRange.tAwal} s/d ${dateRange.tAkhir}</h2>
          <table>
            <thead>
              <tr>
                ${tableHeaders.map((header) => `<th>${header}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    saveAs(blob, `kinerja-${dateRange.tAwal}-${dateRange.tAkhir}.xls`);
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setUserId(user.id);
      setDinasId(user.dinas_id);
      setUnitKerja(user.nama_dinas);

      // Set default dates
      const defaultDates = getDefaultDates();
      setTanggalAwal(defaultDates.tAwal);
      setTanggalAkhir(defaultDates.tAkhir);

      getLibur();
    } else {
      logoutUser(navigate);
    }
  }, []);

  useEffect(() => {
    if (unitKerja && dateRange && dateRange.tAwal && dateRange.tAkhir) {
      fetchKinerjaData();
    }
  }, [unitKerja, dateRange.tAwal, dateRange.tAkhir]);

  useEffect(() => {
    if (listLibur.length > 0 && dateRange.tAwal && dateRange.tAkhir) {
      const d = getDatesWithoutWeekends(
        dateRange.tAwal,
        dateRange.tAkhir,
        listLibur
      );
      const wh = hitungtotaljamkerja(d);
      setTotalHariKerja(d.length);

      setTotalWorkingHours(wh);
    }
  }, [listLibur, dateRange]);

  const formatWorkingHours = (workingHours) => {
    if (!workingHours || workingHours === "0:00") {
      return "0:00";
    }
    return workingHours;
  };

  const formatMinutesToHours = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${mins.toString().padStart(2, "0")}`;
  };

  const columns = [
    {
      Header: "No",
      accessor: "no",
      width: "4%",
      Cell: ({ row }) => row.index + 1,
    },
    {
      Header: "Profil",
      accessor: "profil",
      width: "6%",
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center">
          <img
            src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid}.jpg`}
            alt={row.original.nama}
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
              border: "2px solid #e0e0e0",
            }}
            onError={(e) => {
              // Jika gambar tidak ditemukan, tampilkan icon default
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <MDBox
            display="none"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "grey.200",
              color: "grey.500",
            }}
          >
            <Icon sx={{ fontSize: 20 }}>person</Icon>
          </MDBox>
        </MDBox>
      ),
    },
    {
      Header: "Nama",
      accessor: "nama",
      width: "18%",
    },
    {
      Header: "Hadir",
      accessor: "hadir",
      width: "6%",
      Cell: ({ value }) => (
        <Chip label={value} color="success" size="small" variant="filled" />
      ),
    },
    {
      Header: "Izin",
      accessor: "izin",
      width: "6%",
      Cell: ({ value }) => (
        <Chip label={value} color="warning" size="small" variant="filled" />
      ),
    },
    {
      Header: "Sakit",
      accessor: "sakit",
      width: "6%",
      Cell: ({ value }) => (
        <Chip label={value} color="error" size="small" variant="filled" />
      ),
    },
    {
      Header: "Dinas Luar",
      accessor: "dinasLuar",
      width: "8%",
      Cell: ({ value }) => (
        <Chip label={value} color="info" size="small" variant="filled" />
      ),
    },
    {
      Header: "Tugas Belajar",
      accessor: "tugasBelajar",
      width: "10%",
      Cell: ({ value }) => (
        <Chip label={value} color="secondary" size="small" variant="filled" />
      ),
    },
    {
      Header: "Cuti",
      accessor: "cuti",
      width: "6%",
      Cell: ({ value }) => (
        <Chip label={value} color="default" size="small" variant="filled" />
      ),
    },
    {
      Header: "Jam Kerja",
      accessor: "workingHours",
      width: "10%",
      Cell: ({ value, row }) => (
        <MDBox>
          <MDTypography variant="caption" fontWeight="bold" color="primary">
            {formatWorkingHours(value)}
          </MDTypography>
          {row.index === 0 && (
            <Chip
              label="Terbaik"
              color="success"
              size="small"
              sx={{ ml: 1, fontSize: "0.7rem" }}
            />
          )}
        </MDBox>
      ),
    },
    {
      Header: "Persentase",
      accessor: "percentage",
      width: "16%",
      Cell: ({ row }) => {
        const percentage = calculatePercentage(row.original.workingHours);
        return (
          <MDBox>
            <MDBox display="flex" alignItems="center" mb={1}>
              <MDProgress
                value={percentage}
                color={
                  percentage >= 80
                    ? "success"
                    : percentage >= 60
                    ? "warning"
                    : "error"
                }
                variant="gradient"
                sx={{ flexGrow: 1, mr: 1 }}
              />
              <MDTypography variant="caption" fontWeight="bold" color="text">
                {percentage}%
              </MDTypography>
            </MDBox>
            <MDTypography variant="caption" color="text.secondary">
              {formatWorkingHours(row.original.workingHours)} /{" "}
              {typeof totalWorkingHours === "string"
                ? totalWorkingHours
                : formatMinutesToHours(totalWorkingHours)}
            </MDTypography>
          </MDBox>
        );
      },
    },
    {
      Header: "Aksi",
      accessor: "aksi",
      width: "8%",
      Cell: ({ row }) => (
        <MDButton
          variant="gradient"
          color="info"
          size="small"
          onClick={() => handleDetailClick(row.original)}
          sx={{ minWidth: "auto", px: 1 }}
        >
          <Icon sx={{ fontSize: 16 }}>visibility</Icon>
        </MDButton>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <MDTypography variant="h4">Kinerja Smart DH</MDTypography>
            </Grid>
            <Grid item>
              <MDBox display="flex" gap={1}>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={fetchKinerjaData}
                  disabled={loading}
                >
                  <Icon>refresh</Icon>&nbsp;{loading ? "Memuat..." : "Refresh"}
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={handleExportExcel}
                  disabled={!kinerjaData.length}
                >
                  <Icon>description</Icon>
                  &nbsp;Export Excel
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {/* Rekap Kinerja */}
        <MDBox mb={3}>
          <MDTypography variant="h6" color="text" mb={2}>
            Rekap Kinerja - {dateRange.tAwal} s/d {dateRange.tAkhir}
          </MDTypography>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Chip
                icon={<Icon>group</Icon>}
                label={`Total Pegawai: ${performanceStats.totalPegawai}`}
                color="primary"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>schedule</Icon>}
                label={`Total Jam Kerja Ideal: ${
                  typeof totalWorkingHours === "string"
                    ? totalWorkingHours
                    : formatMinutesToHours(totalWorkingHours)
                }`}
                color="info"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>trending_up</Icon>}
                label={`Rata-rata: ${formatMinutesToHours(
                  performanceStats.rataRataWorkingHours
                )}`}
                color="success"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            {performanceStats.pegawaiTerbaik && (
              <Grid item>
                <Chip
                  icon={<Icon>emoji_events</Icon>}
                  label={`Terbaik: ${performanceStats.pegawaiTerbaik.nama}`}
                  color="warning"
                  variant="filled"
                  sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                />
              </Grid>
            )}
          </Grid>
          <MDBox mt={1}>
            <MDTypography variant="caption" color="text.secondary">
              {unitKerja}
            </MDTypography>
          </MDBox>
        </MDBox>

        <MDBox mb={3}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <MDInput
                type="date"
                label="Tanggal Awal"
                fullWidth
                value={dateRange.tAwal}
                onChange={(e) =>
                  setDateRange({ ...dateRange, tAwal: e.target.value })
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <MDInput
                type="date"
                label="Tanggal Akhir"
                fullWidth
                value={dateRange.tAkhir}
                onChange={(e) =>
                  setDateRange({ ...dateRange, tAkhir: e.target.value })
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <MDButton
                variant="gradient"
                color="success"
                onClick={fetchKinerjaData}
                disabled={loading}
                fullWidth
              >
                <Icon>search</Icon>&nbsp;Cari
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mb={2}>
          <MDTypography variant="h6" color="text">
            Data Kinerja: {dateRange.tAwal} s/d {dateRange.tAkhir}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            Total: {kinerjaData.length} pegawai (Diurutkan berdasarkan jam kerja
            tertinggi)
          </MDTypography>
        </MDBox>

        {isMobile ? (
          <MDBox display="flex" flexDirection="column" gap={1}>
            {loading && (
              <MDTypography variant="button" color="text.secondary">
                Memuat...
              </MDTypography>
            )}
            {!loading &&
              kinerjaData.map((row, index) => {
                const percentage = calculatePercentage(row.workingHours);
                const totalWHText =
                  typeof totalWorkingHours === "string"
                    ? totalWorkingHours
                    : formatMinutesToHours(totalWorkingHours);
                return (
                  <Card key={`${row.uid || row.nama || "row"}-${index}`}>
                    <CardContent>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={4}>
                          <MDBox
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <img
                              src={`https://smartdh.rotendaokab.go.id/uploads/${row.uid}.jpg`}
                              alt={row.nama}
                              style={{
                                width: 72,
                                height: 72,
                                borderRadius: "50%",
                                objectFit: "cover",
                                border: "2px solid #e0e0e0",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                              }}
                            />
                            <MDBox
                              display="none"
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                width: 72,
                                height: 72,
                                borderRadius: "50%",
                                backgroundColor: "grey.200",
                                color: "grey.500",
                              }}
                            >
                              <Icon sx={{ fontSize: 36 }}>person</Icon>
                            </MDBox>
                          </MDBox>
                        </Grid>
                        <Grid item xs={8}>
                          <MDTypography variant="button" fontWeight="bold">
                            {index + 1}. {row.nama}
                          </MDTypography>
                          <MDBox mt={0.5}>
                            <MDBox display="flex" alignItems="center" mb={0.5}>
                              <MDProgress
                                value={percentage}
                                color={
                                  percentage >= 80
                                    ? "success"
                                    : percentage >= 60
                                    ? "warning"
                                    : "error"
                                }
                                variant="gradient"
                                sx={{ flexGrow: 1, mr: 1 }}
                              />
                              <MDTypography
                                variant="caption"
                                fontWeight="bold"
                                color="text"
                              >
                                {percentage}%
                              </MDTypography>
                            </MDBox>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatWorkingHours(row.workingHours)} /{" "}
                              {totalWHText}
                            </MDTypography>
                          </MDBox>
                          <MDBox
                            mt={1}
                            display="flex"
                            flexWrap="wrap"
                            gap={0.5}
                          >
                            <Chip
                              label={`Hadir: ${row.hadir || 0}`}
                              color="success"
                              size="small"
                            />
                            <Chip
                              label={`Izin: ${row.izin || 0}`}
                              color="warning"
                              size="small"
                            />
                            <Chip
                              label={`Sakit: ${row.sakit || 0}`}
                              color="error"
                              size="small"
                            />
                            <Chip
                              label={`Dinas Luar: ${row.dinasLuar || 0}`}
                              color="info"
                              size="small"
                            />
                            <Chip
                              label={`Tugas Belajar: ${row.tugasBelajar || 0}`}
                              color="secondary"
                              size="small"
                            />
                            <Chip
                              label={`Cuti: ${row.cuti || 0}`}
                              size="small"
                            />
                            {index === 0 && (
                              <Chip
                                label="Terbaik"
                                color="success"
                                size="small"
                              />
                            )}
                          </MDBox>
                          <MDBox mt={1}>
                            <MDButton
                              variant="gradient"
                              color="info"
                              size="small"
                              onClick={() => handleDetailClick(row)}
                              sx={{ minWidth: "auto", px: 1 }}
                            >
                              <Icon sx={{ fontSize: 16 }}>visibility</Icon>
                              &nbsp;Detail
                            </MDButton>
                          </MDBox>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })}
          </MDBox>
        ) : (
          <DataTable
            table={{
              columns,
              rows: kinerjaData,
            }}
            entriesPerPage={false}
            showTotalEntries={false}
            isSorted={false}
            noEndBorder
            loading={loading}
          />
        )}

        {kinerjaData.length === 0 && !loading && (
          <MDBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={4}
          >
            <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>
              assessment
            </Icon>
            <MDTypography variant="h6" color="text.secondary">
              Tidak ada data kinerja
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary">
              Coba pilih periode lain atau periksa kembali unit kerja
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default KinerjaDH;
